import { NgModule, Directive, Input, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';

@Component({
  selector: 'p-header',
  template: '<ng-content></ng-content>',
})
export class Header {}

@Component({
  selector: 'p-footer',
  template: '<ng-content></ng-content>',
})
export class Footer {}

@Directive({
  selector: '[pTemplate]',
  host: {},
})
export class PrimeTemplate {
  @Input() type!: string;

  @Input('pTemplate') name!: string;

  constructor(public template: TemplateRef<any>) {}

  getType(): string {
    return this.name;
  }
}
export interface SelectItem<T = any> {
  label?: string;
  value: T;
  styleClass?: string;
  icon?: string;
  title?: string;
  disabled?: boolean;
}
export class TranslationKeys {
  public static readonly STARTS_WITH = 'startsWith';
  public static readonly CONTAINS = 'contains';
  public static readonly NOT_CONTAINS = 'notContains';
  public static readonly ENDS_WITH = 'endsWith';
  public static readonly EQUALS = 'equals';
  public static readonly NOT_EQUALS = 'notEquals';
  public static readonly NO_FILTER = 'noFilter';
  public static readonly LT = 'lt';
  public static readonly LTE = 'lte';
  public static readonly GT = 'gt';
  public static readonly GTE = 'gte';
  public static readonly IS = 'is';
  public static readonly IS_NOT = 'isNot';
  public static readonly BEFORE = 'before';
  public static readonly AFTER = 'after';
  public static readonly CLEAR = 'clear';
  public static readonly APPLY = 'apply';
  public static readonly MATCH_ALL = 'matchAll';
  public static readonly MATCH_ANY = 'matchAny';
  public static readonly ADD_RULE = 'addRule';
  public static readonly REMOVE_RULE = 'removeRule';
  public static readonly ACCEPT = 'accept';
  public static readonly REJECT = 'reject';
  public static readonly CHOOSE = 'choose';
  public static readonly UPLOAD = 'upload';
  public static readonly CANCEL = 'cancel';
  public static readonly DAY_NAMES = 'dayNames';
  public static readonly DAY_NAMES_SHORT = 'dayNamesShort';
  public static readonly DAY_NAMES_MIN = 'dayNamesMin';
  public static readonly MONTH_NAMES = 'monthNames';
  public static readonly MONTH_NAMES_SHORT = 'monthNamesShort';
  public static readonly FIRST_DAY_OF_WEEK = 'firstDayOfWeek';
  public static readonly TODAY = 'today';
  public static readonly WEEK_HEADER = 'weekHeader';
  public static readonly WEAK = 'weak';
  public static readonly MEDIUM = 'medium';
  public static readonly STRONG = 'strong';
  public static readonly PASSWORD_PROMPT = 'passwordPrompt';
  public static readonly EMPTY_MESSAGE = 'emptyMessage';
  public static readonly EMPTY_FILTER_MESSAGE = 'emptyFilterMessage';
}

import { AnimationEvent } from '@angular/animations';

export type OverlayModeType = 'modal' | 'overlay' | undefined;

export type ResponsiveOverlayDirectionType =
  | 'center'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | undefined;

export interface OverlayListenerOptions {
  type?: 'scroll' | 'outside' | 'resize' | undefined;
  mode?: OverlayModeType;
  valid?: boolean;
}

export interface ResponsiveOverlayOptions {
  style?: any;
  styleClass?: string;
  contentStyle?: any;
  contentStyleClass?: string;
  breakpoint?: string;
  media?: string;
  direction?: ResponsiveOverlayDirectionType;
}

export interface OverlayOnShowEvent {
  overlay?: HTMLElement | undefined;
  target?: HTMLElement | undefined;
  mode?: OverlayModeType;
}

export interface OverlayOnBeforeShowEvent extends OverlayOnShowEvent {}

export interface OverlayOnBeforeHideEvent extends OverlayOnBeforeShowEvent {}

export interface OverlayOnHideEvent extends OverlayOnShowEvent {}

export interface OverlayOptions {
  mode?: OverlayModeType;
  style?: any;
  styleClass?: string;
  contentStyle?: any;
  contentStyleClass?: string;
  target?: any;
  appendTo?: 'body' | HTMLElement | undefined;
  autoZIndex?: boolean;
  baseZIndex?: number;
  hideOnEscape?: boolean;
  showTransitionOptions?: string;
  hideTransitionOptions?: string;
  listener?: (event: Event, options?: OverlayListenerOptions) => boolean | void;
  responsive?: ResponsiveOverlayOptions | undefined;
  onBeforeShow?: (event?: OverlayOnBeforeShowEvent) => void;
  onShow?: (event?: OverlayOnShowEvent) => void;
  onBeforeHide?: (event?: OverlayOnBeforeHideEvent) => void;
  onHide?: (event?: OverlayOnHideEvent) => void;
  onAnimationStart?: (event?: AnimationEvent) => void;
  oAnimationDone?: (event?: AnimationEvent) => void;
}
@NgModule({
  imports: [CommonModule],
  exports: [Header, Footer, PrimeTemplate],
  declarations: [Header, Footer, PrimeTemplate],
})
export class SharedModule {}

export interface ScrollerOptions {
   /**
    * Unique identifier of the element.
    */
   id?: string | undefined;
   /**
    * Inline style of the component.
    */
   style?: { [klass: string]: any } | null | undefined;
   /**
    * Style class of the element.
    */
   styleClass?: string | undefined;
   /**
    * Index of the element in tabbing order.
    */
   tabindex?: number | undefined;
   /**
    * An array of objects to display.
    */
   items?: any[];
   /**
    * The height/width of item according to orientation.
    */
   itemSize?: any;
   /**
    * Height of the scroll viewport.
    */
   scrollHeight?: string | undefined;
   /**
    * Width of the scroll viewport.
    */
   scrollWidth?: string | undefined;
   /**
    * The orientation of scrollbar.
    */
   orientation?: 'vertical' | 'horizontal' | 'both';
   /**
    * Used to specify how many items to load in each load method in lazy mode.
    */
   step?: number | undefined;
   /**
    * Delay in scroll before new data is loaded.
    */
   delay?: number | undefined;
   /**
    * Delay after window's resize finishes.
    */
   resizeDelay?: number | undefined;
   /**
    * Used to append each loaded item to top without removing any items from the DOM. Using very large data may cause the browser to crash.
    */
   appendOnly?: boolean;
   /**
    * Specifies whether the scroller should be displayed inline or not.
    */
   inline?: boolean;
   /**
    * Defines if data is loaded and interacted with in lazy manner.
    */
   lazy?: boolean;
   /**
    * If disabled, the scroller feature is eliminated and the content is displayed directly.
    */
   disabled?: boolean;
   /**
    * Used to implement a custom loader instead of using the loader feature in the scroller.
    */
   loaderDisabled?: boolean;
   /**
    * Columns to display.
    */
   columns?: any[] | undefined;
   /**
    * Used to implement a custom spacer instead of using the spacer feature in the scroller.
    */
   showSpacer?: boolean;
   /**
    * Defines whether to show loader.
    */
   showLoader?: boolean;
   /**
    * Determines how many additional elements to add to the DOM outside of the view. According to the scrolls made up and down, extra items are added in a certain algorithm in the form of multiples of this number. Default value is half the number of items shown in the view.
    */
   numToleratedItems?: any;
   /**
    * Defines whether the data is loaded.
    */
   loading?: boolean;
   /**
    * Defines whether to dynamically change the height or width of scrollable container.
    */
   autoSize?: boolean;
   /**
    * Function to optimize the dom operations by delegating to ngForTrackBy, default algoritm checks for object identity.
    */
   trackBy?: Function;
   /**
    * Callback to invoke in lazy mode to load new data.
    */
   onLazyLoad?: Function | undefined;
   /**
    * Callback to invoke when scroll position changes.
    */
   onScroll?: Function | undefined;
   /**
    * Callback to invoke when scroll position and item's range in view changes.
    */
   onScrollIndexChange?: Function | undefined;
}